import { useMutation } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { fetchJsonWithInit } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import type {
  SharePointResolveUrlRequest,
  SharePointResolvedLocationResponse,
} from '@/hooks/sharepoint/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

interface UseResolveSharepointLinkArgs {
  orgId: UUID | ''
}

export const resolveSharepointLink = async (
  orgId: UUID | '',
  accessToken: string,
  request: SharePointResolveUrlRequest,
): Promise<SharePointResolvedLocationResponse> => {
  if (!orgId) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'No org ID provided.',
    })
  }

  const url = `${API_URL}/api/v1/organizations/${orgId}/sharepoint/resolve-url`
  return fetchJsonWithInit<SharePointResolvedLocationResponse>(
    url,
    accessToken,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    },
  )
}

export const useResolveSharepointLink = ({
  orgId,
}: UseResolveSharepointLinkArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useMutation({
    mutationFn: (request: SharePointResolveUrlRequest) =>
      resolveSharepointLink(orgId, accessToken, request),
  })
}
